import * as MUI from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { trimText } from "../../../../utils/Functions";
import { MATERIAL } from "./ReferralType";

const CELL_TRIM_LENGTH = 40;

function TableHeader({ columns, order, orderBy, onSort }) {
  const { t } = useTranslation();

  function handleSort(columnId) {
    if (!onSort) return;
    const isAsc = orderBy === columnId && order === "asc";
    onSort(columnId, isAsc ? "desc" : "asc");
  }

  return (
    <MUI.TableHead>
      <MUI.TableRow
        sx={{
          "& th": {
            bgcolor: (theme) =>
              theme.palette.mode === "light"
                ? "grey.50"
                : "rgba(255,255,255,0.04)",
            fontWeight: 700,
            fontSize: "0.75rem",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            color: "text.secondary",
            borderBottom: "2px solid",
            borderBottomColor: "divider",
          },
        }}
      >
        {columns.map((column) => (
          <MUI.TableCell
            key={column.id}
            align={column.align}
            sx={{ width: column.width }}
            sortDirection={orderBy === column.id ? order : false}
          >
            {column.id === "index" ? (
              ""
            ) : column.sortable ? (
              <MUI.TableSortLabel
                active={orderBy === column.id}
                direction={orderBy === column.id ? order : "asc"}
                onClick={() => handleSort(column.id)}
              >
                {t(column.label)}
              </MUI.TableSortLabel>
            ) : (
              t(column.label)
            )}
          </MUI.TableCell>
        ))}
      </MUI.TableRow>
    </MUI.TableHead>
  );
}

function LoadingRow({ colSpan }) {
  return (
    <MUI.TableRow>
      <MUI.TableCell colSpan={colSpan} sx={{ border: 0, py: 6 }}>
        <MUI.Stack alignItems="center" justifyContent="center">
          <MUI.CircularProgress size={28} />
        </MUI.Stack>
      </MUI.TableCell>
    </MUI.TableRow>
  );
}

function EmptyRow({ colSpan, emptyMessage }) {
  const { t } = useTranslation();

  return (
    <MUI.TableRow>
      <MUI.TableCell colSpan={colSpan} sx={{ border: 0, py: 3 }}>
        <MUI.Typography variant="body1" color={"grayText"}>
          {t(emptyMessage)}
        </MUI.Typography>
      </MUI.TableCell>
    </MUI.TableRow>
  );
}

function ReferralRow({ item, index, secondaryColumns, onRowClick }) {
  return (
    <MUI.TableRow
      hover
      onClick={() => onRowClick(item)}
      sx={{
        cursor: "pointer",
        "&:last-child td": { borderBottom: 0 },
      }}
    >
      <MUI.TableCell sx={{ color: "text.disabled" }}>
        {index + 1}
      </MUI.TableCell>
      <MUI.TableCell>
        <MUI.Tooltip title={item.title}>
          <MUI.Typography variant="body2" sx={{ fontWeight: 600 }}>
            {trimText(item.title, CELL_TRIM_LENGTH)}
          </MUI.Typography>
        </MUI.Tooltip>
      </MUI.TableCell>
      {secondaryColumns.map((column) => (
        <MUI.TableCell key={column.id}>
          <MUI.Typography variant="body2" color="text.secondary">
            {trimText(column.getValue ? column.getValue(item) : item[column.id], CELL_TRIM_LENGTH) || "-"}
          </MUI.Typography>
        </MUI.TableCell>
      ))}
    </MUI.TableRow>
  );
}

export default function ReferralsTable({
  items,
  referralType,
  isLoading,
  emptyMessage,
  onRowClick,
  order,
  orderBy,
  onSort,
}) {
  const columns = [
    { id: "index", label: "#", align: "left", width: 56, sortable: false },
    { id: "title", label: "Title", align: "left", sortable: true },
    {
      id: "institution",
      label: "Institution",
      align: "left",
      sortable: false,
      getValue: (item) => item.institution?.name,
    },
    { id: "diagnosis", label: "Diagnosis", align: "left", sortable: false },
    { id: "requestedExam", label: "RequestedExam", align: "left", sortable: false },
    ...(referralType === MATERIAL
      ? [{ id: "material", label: "Material", align: "left", sortable: false }]
      : []),
  ];
  const secondaryColumns = columns.filter((column) => column.id !== "index" && column.id !== "title");

  return (
    <MUI.Paper
      variant="outlined"
      sx={{
        borderRadius: 3,
        borderColor: "divider",
        overflow: "hidden",
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      <MUI.TableContainer sx={{ overflowX: "auto", minWidth: 0 }}>
        <MUI.Table sx={{ minWidth: 700 }}>
          <TableHeader columns={columns} order={order} orderBy={orderBy} onSort={onSort} />
          <MUI.TableBody>
            {isLoading ? (
              <LoadingRow colSpan={columns.length} />
            ) : items.length === 0 ? (
              <EmptyRow colSpan={columns.length} emptyMessage={emptyMessage} />
            ) : (
              items.map((item, index) => (
                <ReferralRow
                  key={item.id}
                  item={item}
                  index={index}
                  secondaryColumns={secondaryColumns}
                  onRowClick={onRowClick}
                />
              ))
            )}
          </MUI.TableBody>
        </MUI.Table>
      </MUI.TableContainer>
    </MUI.Paper>
  );
}
